import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import Section from '../components/Section.jsx';
import { fetchCondominiums } from '../api.js';
import { config } from '../config.js';

const STATES = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT',
  'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
];

/**
 * Busca pública de condomínios.
 *
 * É por aqui que o fornecedor descobre onde pode atuar. A consulta usa a rota
 * pública `GET /condominiums`, que devolve apenas dados não sensíveis
 * (nome, cidade, bairro e volume de moradores).
 */
export default function CondominiumFinder() {
  const [filters, setFilters] = useState({ state: '', city: '', search: '' });
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const query = useMemo(() => filters, [filters]);

  useEffect(() => {
    const controller = new AbortController();
    // Debounce curto: o campo de busca dispara a cada tecla.
    const timer = setTimeout(() => {
      setError(null);
      fetchCondominiums({ ...query, signal: controller.signal })
        .then((data) => setItems(data.items ?? []))
        .catch((err) => {
          if (err.name !== 'AbortError') {
            setError(err.message);
            setItems([]);
          }
        });
    }, 350);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const update = useCallback(
    (field) => (event) => setFilters((current) => ({ ...current, [field]: event.target.value })),
    [],
  );

  return (
    <Section
      id="condominios"
      eyebrow="Para fornecedores"
      title="Encontre condomínios e solicite adesão"
      subtitle="Escolha a região, veja o alcance de cada condomínio e envie a solicitação. A liberação passa pelo síndico ou pelo conselho antes de qualquer oferta aparecer."
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
        <TextField
          select
          label="Estado"
          value={filters.state}
          onChange={update('state')}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {STATES.map((state) => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Cidade"
          value={filters.city}
          onChange={update('city')}
          placeholder="São Paulo"
          disabled={!filters.state}
          helperText={!filters.state ? 'Selecione o estado primeiro' : ' '}
          sx={{ minWidth: 200 }}
        />

        <TextField
          label="Nome do condomínio"
          value={filters.search}
          onChange={update('search')}
          placeholder="Residencial Jardim"
          fullWidth
        />
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Não foi possível buscar condomínios agora ({error}).
        </Alert>
      )}

      {items?.length === 0 && !error && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Nenhum condomínio encontrado com esses filtros. Seu condomínio ainda não está aqui?{' '}
          <Box component="a" href={`mailto:${config.contactEmail}`} sx={{ color: 'inherit' }}>
            fale com a gente
          </Box>
          .
        </Alert>
      )}

      <Grid container spacing={3}>
        {(items ?? Array.from({ length: 3 })).map((condominium, index) => (
          <Grid key={condominium?.id ?? index} size={{ xs: 12, sm: 6, md: 4 }}>
            {condominium ? (
              <Card elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {condominium.name}
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
                    <LocationCityIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {[condominium.neighborhood, condominium.city, condominium.state]
                        .filter(Boolean)
                        .join(', ') || 'Localização não informada'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
                    <PeopleAltIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                    <Typography variant="body2">
                      {condominium.residentCount} moradores em {condominium.unitCount} unidades
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                    <Chip
                      size="small"
                      label={condominium.acceptingSuppliers ? 'Aceita fornecedores' : 'Fechado no momento'}
                      color={condominium.acceptingSuppliers ? 'success' : 'default'}
                      variant={condominium.acceptingSuppliers ? 'filled' : 'outlined'}
                    />
                    <Button
                      size="small"
                      href={`${config.appUrl}/fornecedor/adesoes?condominio=${condominium.id}`}
                      disabled={!condominium.acceptingSuppliers}
                    >
                      Solicitar adesão
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Skeleton variant="rounded" height={196} />
            )}
          </Grid>
        ))}
      </Grid>
    </Section>
  );
}
